import { getEntityLocalID as localId } from "@/utils";
import type {
	FundExplorerAPIResponse,
	MetaField,
	TypedAPIObj,
} from "../api.types";
import type {
	Datum,
	Formatter,
	Fund,
	IndexedData,
	Profile,
	ShareClass,
} from "../types";

export default function transformer(
	formatter: Formatter,
	jsonResponse: FundExplorerAPIResponse,
): IndexedData {
	const processor = processData.bind(null, formatter);
	const shareclasses: IndexedData = {};
	const funds: IndexedData = jsonResponse.funds
		.map(
			({
				data,
				share_classes,
				profile_ids,
				primary_shareclass,
				currency,
				id,
				fund_badge_info,
			}) => {
				const fundShareClasses: ShareClass[] = share_classes.map(
					({ id: scId, data, currency }) => ({
						id: scId,
						fundId: localId("fund", id),
						localId: localId("shareclass", scId),
						listings: [],
						...processor(jsonResponse.metadata.share_class_fields, {
							type: "shareclass",
							shareclass: { id: scId, data, currency },
						}),
					}),
				);

				for (const fsc of fundShareClasses) {
					shareclasses[fsc.localId] = {
						type: "shareclass",
						data: fsc,
					};
				}

				return {
					id,
					localId: localId("fund", id),
					shareclasses: fundShareClasses,
					profiles: profile_ids?.map((id) => localId("profile", id)) ?? [],
					...processor(jsonResponse.metadata.fund_fields, {
						type: "fund",
						fund: {
							id,
							data,
							share_classes,
							primary_shareclass,
							currency,
							fund_badge_info,
						},
					}),
					currency,
					fund_badge_info,
				} as Fund;
			},
		)
		.reduce((acc, fund) => {
			acc[fund.localId] = { type: "fund", data: fund };
			return acc;
		}, {} as IndexedData);
	const profilesData = jsonResponse.profiles;
	const profilesMetaData = jsonResponse.metadata.profile_fields;
	let profiles: IndexedData = {};
	if (profilesData && profilesMetaData) {
		profiles = profilesData
			.map((profile) => ({
				localId: localId("profile", profile.id),
				id: profile.id,
				funds: jsonResponse.funds
					.filter((fund) => fund.profile_ids?.includes(profile.id))
					.map((fund) => localId("fund", fund.id)),
				...processor(profilesMetaData, {
					type: "profile",
					profile,
				}),
			}))
			.reduce((acc, profile) => {
				acc[profile.localId] = { type: "profile", data: profile };
				return acc;
			}, {} as IndexedData);
	}

	return {
		...funds,
		...shareclasses,
		...profiles,
	};
}

function processData(
	formatter: Formatter,
	meta: MetaField[],
	obj: TypedAPIObj,
) {
	const data =
		obj.type === "fund"
			? obj.fund.data
			: obj.type === "shareclass"
				? obj.shareclass.data
				: obj.profile.data;

	const retObj: Omit<Datum, "localId"> = {};
	for (let i = 0; i < meta.length; i += 1) {
		retObj[meta[i].code_name] = formatter(meta[i], obj, data[i]);
		retObj[`__${meta[i].code_name}`] = data[i];
	}
	return retObj;
}
